import { useState } from 'react';
import { useHistory } from 'react-router-dom';
import { toast } from '../../../../ui/toast';
import { updateLicense } from '../../../../utils/api_service/admin';
import { createOrganization } from '../../../../utils/api_service/organization';
import type {
  LicenseDateRange,
  OrganizationCreate,
  OrganizationLicenses,
} from '../../../../utils/types/admin_types';
import {
  CREATE_DISABLED_TOOLTIP_INCOMPLETE,
  CREATE_DISABLED_TOOLTIP_LICENSES,
  CREATE_LICENSE_CATALOG,
} from '../constants';
import { areLicenseDatesComplete, formatMonthlyCredits, parseDomainsInput, parseMonthlyCredits } from '../functions';
import type { BillingInterval, ILicenseCatalogItem, LicenseCatalogKey } from '../interfaces';
import type { ILicenseDateValue } from '../../components/LicenseCard';

export interface IUseCreateOrganization {
  formData: OrganizationCreate;
  billingInterval: BillingInterval;
  catalog: ILicenseCatalogItem[];
  organizationLicenses: OrganizationLicenses;
  isCreateDisabled: boolean;
  disabledTooltip: string;
  handleFieldChange: (field: keyof OrganizationCreate, value: OrganizationCreate[keyof OrganizationCreate]) => void;
  handleDomainsChange: (value: string) => void;
  handleBillingIntervalChange: (interval: BillingInterval) => void;
  isLicenseActive: (key: LicenseCatalogKey) => boolean;
  toggleLicense: (key: LicenseCatalogKey) => void;
  licenseStartDate: (key: LicenseCatalogKey) => string | null;
  licenseEndDate: (key: LicenseCatalogKey) => string | null;
  updateLicenseDate: (key: LicenseCatalogKey, field: keyof LicenseDateRange, value: ILicenseDateValue) => void;
  monthlyCreditsValue: (key: LicenseCatalogKey) => string;
  updateMonthlyCredits: (key: LicenseCatalogKey, raw: string) => void;
  handleCreateOrganization: () => Promise<void>;
  handleCancel: () => void;
}

const EMPTY_LICENSES: OrganizationLicenses = {
  organizationId: '',
  hierTalentBase: undefined,
  hierTalentAutoAssessments: undefined,
  hierTalentAutoClassify: undefined,
  hierTalentJobPost: undefined,
  hierTalentSmartInterviews: undefined,
  hierTalentCandiBank: undefined,
  hierTalentCca: undefined,
  hierTalentPsychometricFaxtor: undefined,
  hierTalentWellness: undefined,
  hierTalentReporting: undefined,
};

export const useCreateOrganization = (): IUseCreateOrganization => {
  const history = useHistory();
  const [billingInterval, setBillingInterval] = useState<BillingInterval>('monthly');
  const [organizationLicenses, setOrganizationLicenses] = useState<OrganizationLicenses>(EMPTY_LICENSES);
  const [formData, setFormData] = useState<OrganizationCreate>({
    organization_name: '',
    organization_domains: [],
    first_name: '',
    last_name: '',
    email: '',
    interval: 'month',
    company_size: 'Small',
  });

  const handleFieldChange = (
    field: keyof OrganizationCreate,
    value: OrganizationCreate[keyof OrganizationCreate],
  ): void => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleDomainsChange = (value: string): void => {
    setFormData((prev) => ({ ...prev, organization_domains: parseDomainsInput(value) }));
  };

  const handleBillingIntervalChange = (interval: BillingInterval): void => {
    setBillingInterval(interval);
    handleFieldChange('interval', interval === 'monthly' ? 'month' : 'year');
  };

  const isLicenseActive = (key: LicenseCatalogKey): boolean =>
    organizationLicenses[key] !== undefined;

  const toggleLicense = (key: LicenseCatalogKey): void => {
    setOrganizationLicenses((prev) => ({
      ...prev,
      [key]: prev[key] ? undefined : { startDate: '', endDate: '' },
    }));
  };

  const licenseStartDate = (key: LicenseCatalogKey): string | null =>
    organizationLicenses[key]?.startDate ?? null;

  const licenseEndDate = (key: LicenseCatalogKey): string | null =>
    organizationLicenses[key]?.endDate ?? null;

  const updateLicenseDate = (
    key: LicenseCatalogKey,
    field: keyof LicenseDateRange,
    value: ILicenseDateValue,
  ): void => {
    if (field === 'monthlyCredits') return;
    const dateString = field === 'startDate' ? value.startDate ?? '' : value.endDate ?? '';
    setOrganizationLicenses((prev) => ({
      ...prev,
      [key]: { ...prev[key], [field]: dateString },
    }));
  };

  const monthlyCreditsValue = (key: LicenseCatalogKey): string =>
    formatMonthlyCredits(organizationLicenses, key);

  const updateMonthlyCredits = (key: LicenseCatalogKey, raw: string): void => {
    const parsed = parseMonthlyCredits(raw);
    setOrganizationLicenses((prev) => ({
      ...prev,
      [key]: { ...(prev[key] ?? { startDate: '', endDate: '' }), monthlyCredits: parsed },
    }));
  };

  const isFormIncomplete =
    !formData.organization_name.trim() ||
    formData.organization_domains.length === 0 ||
    !formData.first_name.trim() ||
    !formData.last_name.trim() ||
    !formData.email.trim();

  const licenseKeys = CREATE_LICENSE_CATALOG.map((item) => item.licenseKey);
  const isCreateDisabled = isFormIncomplete || !areLicenseDatesComplete(organizationLicenses, licenseKeys);
  const disabledTooltip = isFormIncomplete
    ? CREATE_DISABLED_TOOLTIP_INCOMPLETE
    : CREATE_DISABLED_TOOLTIP_LICENSES;

  const handleCreateOrganization = async (): Promise<void> => {
    try {
      const res = await createOrganization(formData);
      if (res.ok) {
        const response = await res.json();
        const organizationId = response.organization.id;

        toast.success('Organization created.', { position: 'bottom-center' });

        const updatedLicenses = { ...organizationLicenses, organizationId };
        const licenseRes = await updateLicense(updatedLicenses);

        if (licenseRes.ok) {
          toast.success('License updated successfully.', { position: 'bottom-center' });
        }
      }
    } catch (e) {
      toast.error('Error creating organization or updating license.', {
        position: 'bottom-center',
      });
    }
  };

  const handleCancel = (): void => {
    history.push('/organizations');
  };

  return {
    formData,
    billingInterval,
    catalog: CREATE_LICENSE_CATALOG,
    organizationLicenses,
    isCreateDisabled,
    disabledTooltip,
    handleFieldChange,
    handleDomainsChange,
    handleBillingIntervalChange,
    isLicenseActive,
    toggleLicense,
    licenseStartDate,
    licenseEndDate,
    updateLicenseDate,
    monthlyCreditsValue,
    updateMonthlyCredits,
    handleCreateOrganization,
    handleCancel,
  };
};
